import React, { useMemo, useState, useRef, useEffect } from 'react';
import { ChevronDownIcon, PlusIcon, MinusIcon, BuildingOfficeIcon, FunnelIcon, ArrowsPointingOutIcon, ArrowsPointingInIcon } from '@heroicons/react/24/outline';
import type { Colaborador, Departamento, Flow } from '../../types';
import ColaboradorPopover from './ColaboradorPopover';

interface OrganogramaProps {
  departamentos: Departamento[];
  colaboradores: Colaborador[];
  flows: Flow[];
  onNavigateToColaborador: (colaborador: Colaborador) => void;
  onNavigateToFlow: (flow: Flow) => void;
  userPermissions?: {
    canEdit: boolean;
    canAdmin: boolean;
    currentUser?: any;
  };
  cardsVencidos?: { [colaboradorId: string]: number };
}

interface ColaboradorNode {
  colaborador: Colaborador;
  subordinados: ColaboradorNode[];
  cardsVencidos: number;
  totalCardsHierarquia: number;
}

interface CardVencido {
  id: string | number;
  titulo: string;
  etapaNome: string;
  flowNome: string;
  dataVencimento: string;
  diasVencido: number;
}

type CardsTipo = 'proprios' | 'equipe';

interface ShowingCardsState {
  colaboradorId: string;
  tipo: CardsTipo;
  position: { x: number; y: number };
}

const getInitials = (name: string) => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'NN';
  const first = parts[0][0] || '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase();
};

const colorClasses = [
  'bg-gradient-to-br from-indigo-500 to-blue-500',
  'bg-gradient-to-br from-purple-500 to-pink-500',
  'bg-gradient-to-br from-emerald-500 to-teal-500',
  'bg-gradient-to-br from-amber-500 to-orange-500',
  'bg-gradient-to-br from-sky-500 to-cyan-500',
  'bg-gradient-to-br from-rose-500 to-red-500',
];

const getColorFromString = (s: string) => {
  let hash = 0;
  for (let i = 0; i < s.length; i++) hash = (hash << 5) - hash + s.charCodeAt(i);
  const idx = Math.abs(hash) % colorClasses.length;
  return colorClasses[idx];
};

function Organograma({
  departamentos,
  colaboradores,
  flows,
  onNavigateToColaborador,
  onNavigateToFlow,
  userPermissions,
  cardsVencidos = {},
}: OrganogramaProps) {
  const [zoom, setZoom] = useState(1);
  const [expandedNodes, setExpandedNodes] = useState<string[]>([]);
  const [selectedColaborador, setSelectedColaborador] = useState<Colaborador | null>(null);
  const [showingCardsVencidos, setShowingCardsVencidos] = useState<ShowingCardsState | null>(null);
  const [selectedDepartamento, setSelectedDepartamento] = useState<string>('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Detectar mudanças no estado de fullscreen
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.2, 2));
  const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.2, 0.5));

  const toggleFullscreen = async () => {
    if (!containerRef.current) return;

    try {
      if (!document.fullscreenElement) {
        await containerRef.current.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (error) {
      console.error('Erro ao alternar fullscreen:', error);
    }
  };

  const filteredColaboradores = useMemo(() => {
    if (!Array.isArray(colaboradores)) return [] as Colaborador[];
    if (!selectedDepartamento) return colaboradores;
    return colaboradores.filter((c) => String(c?.departamentoId) === String(selectedDepartamento));
  }, [colaboradores, selectedDepartamento]);

  const calcularCardsHierarquia = (
    colaboradorId: string,
    nodesMap: Map<string, ColaboradorNode>
  ): number => {
    const node = nodesMap.get(colaboradorId);
    if (!node) return 0;

    let total = node.cardsVencidos;
    node.subordinados.forEach((sub) => {
      total += calcularCardsHierarquia(sub.colaborador.id, nodesMap);
    });
    return total;
  };

  const hierarchy: ColaboradorNode[] = useMemo(() => {
    const source = filteredColaboradores;
    if (!source || !Array.isArray(source)) {
      console.warn('⚠️ Organograma: colaboradores não é um array válido');
      return [];
    }
    const nodesMap = new Map<string, ColaboradorNode>();
    source.forEach((col) => {
      if (col && col.id) {
        nodesMap.set(col.id, {
          colaborador: col,
          subordinados: [],
          cardsVencidos: cardsVencidos?.[col.id] ?? 0,
          totalCardsHierarquia: 0,
        });
      }
    });
    source.forEach((col) => {
      if (col && col.superiorId) {
        const superiorNode = nodesMap.get(col.superiorId);
        const currentNode = nodesMap.get(col.id);
        if (superiorNode && currentNode) superiorNode.subordinados.push(currentNode);
      }
    });
    nodesMap.forEach((node, id) => {
      node.totalCardsHierarquia = calcularCardsHierarquia(id, nodesMap);
    });
    // Roots: aqueles sem superior ou cujo superior não está no conjunto filtrado
    const roots = source.filter(
      (c) => !c?.superiorId || !source.find((s) => String(s.id) === String(c.superiorId))
    );
    return roots
      .map((c) => nodesMap.get(c.id)!)
      .filter(Boolean)
      .sort((a, b) => (b.colaborador.isGestor ? 1 : 0) - (a.colaborador.isGestor ? 1 : 0));
  }, [filteredColaboradores, cardsVencidos]);

  const toggleNode = (node: ColaboradorNode, event: React.MouseEvent) => {
    event.stopPropagation();
    setExpandedNodes((prev) =>
      prev.includes(node.colaborador.id)
        ? prev.filter((id) => id !== node.colaborador.id)
        : [...prev, node.colaborador.id]
    );
  };

  const handleColaboradorClick = (colaborador: Colaborador, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedColaborador((prev) => (prev?.id === colaborador.id ? null : colaborador));
  };

  const handleClickOutside = () => {
    setSelectedColaborador(null);
    setShowingCardsVencidos(null);
  };

  const handleNotificacaoClick = (
    colaboradorId: string,
    tipo: CardsTipo,
    event: React.MouseEvent
  ) => {
    event.stopPropagation();
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    setShowingCardsVencidos({
      colaboradorId,
      tipo,
      position: { x: rect.left + window.scrollX, y: rect.bottom + window.scrollY + 10 },
    });
  };

  const getAllSubordinados = (node: ColaboradorNode): ColaboradorNode[] => {
    let todos: ColaboradorNode[] = [];
    node.subordinados.forEach((sub) => {
      todos.push(sub);
      todos = todos.concat(getAllSubordinados(sub));
    });
    return todos;
  };

  const getCardsVencidosDetalhes = (
    colaboradorId: string,
    tipo: CardsTipo
  ): CardVencido[] => {
    const cardsDetalhes: CardVencido[] = [];
    const processarColaborador = (colabId: string) => {
      flows.forEach((flow) => {
        flow.cards?.forEach((card: any) => {
          const etapa = flow.etapas?.find((e: any) => String(e.id) === String(card.etapaId));
          if (!etapa) return;
          
          // Verificar se este card pertence ao colaborador:
          // 1. Se tem responsável individual, deve ser o responsável
          // 2. Se não tem responsável individual, verifica se é participante da etapa
          const pertenceAoColaborador = card.responsavelIndividualId
            ? String(card.responsavelIndividualId) === String(colabId)
            : etapa.participantes?.map((p: any) => String(p)).includes(String(colabId));
          
          if (!pertenceAoColaborador) return;
          
          const campoVencimento = etapa.campos.find((campo: any) => campo.tipo === 'data_vencimento');
          if (!campoVencimento) return;
          const dataVencimento = card.campos?.[campoVencimento.id];
          if (!dataVencimento) return;
          try {
            const vencimento = new Date(dataVencimento);
            const agora = new Date();
            if (vencimento < agora) {
              const diasVencido = Math.floor(
                (agora.getTime() - vencimento.getTime()) / (1000 * 60 * 60 * 24)
              );
              cardsDetalhes.push({
                id: card.id,
                titulo: card.titulo,
                etapaNome: etapa.nome,
                flowNome: flow.name,
                dataVencimento: vencimento.toLocaleDateString('pt-BR'),
                diasVencido,
              });
            }
          } catch (error) {
            console.warn('Erro ao processar data de vencimento:', error);
          }
        });
      });
    };
    if (tipo === 'proprios') {
      processarColaborador(colaboradorId);
    } else {
      const buscarSubordinados = (nodeId: string) => {
        const node =
          hierarchy.find((n) => n.colaborador.id === nodeId) ||
          hierarchy.flatMap((n) => getAllSubordinados(n)).find((n) => n.colaborador.id === nodeId);
        node?.subordinados.forEach((sub) => {
          processarColaborador(sub.colaborador.id);
          buscarSubordinados(sub.colaborador.id);
        });
      };
      buscarSubordinados(colaboradorId);
    }
    return cardsDetalhes.sort((a, b) => b.diasVencido - a.diasVencido);
  };

  const renderCardsVencidosPopover = () => {
    if (!showingCardsVencidos) return null;
    const { colaboradorId, tipo, position } = showingCardsVencidos;
    const cardsDetalhes = getCardsVencidosDetalhes(colaboradorId, tipo);
    const colaborador = colaboradores.find((c) => c.id === colaboradorId);
    if (cardsDetalhes.length === 0) return null;
    return (
      <div
        className="fixed z-50 bg-white rounded-lg shadow-xl border border-gray-200 w-96 max-h-96 overflow-y-auto"
        style={{ left: Math.min(position.x, window.innerWidth - 400), top: position.y }}
      >
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">
              {tipo === 'proprios' ? '🚨 Cards Vencidos' : '⚠️ Cards Vencidos da Equipe'}
            </h3>
            <button onClick={() => setShowingCardsVencidos(null)} className="text-gray-400 hover:text-gray-600 p-1 rounded">
              ✕
            </button>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            {colaborador?.nome} - {cardsDetalhes.length} card{cardsDetalhes.length !== 1 ? 's' : ''} vencido{cardsDetalhes.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="p-4 space-y-3">
          {cardsDetalhes.map((card) => (
            <div key={card.id} className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 text-sm">{card.titulo}</h4>
                  <p className="text-xs text-gray-600 mt-1">
                    <span className="font-medium">Fluxo:</span> {card.flowNome}
                  </p>
                  <p className="text-xs text-gray-600">
                    <span className="font-medium">Etapa:</span> {card.etapaNome}
                  </p>
                  <p className="text-xs text-gray-600">
                    <span className="font-medium">Vencimento:</span> {card.dataVencimento}
                  </p>
                </div>
                <div className="ml-3 text-right">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      card.diasVencido > 7
                        ? 'bg-red-100 text-red-800'
                        : card.diasVencido > 3
                        ? 'bg-orange-100 text-orange-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}
                  >
                    {card.diasVencido} dia{card.diasVencido !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderColaborador = (node: ColaboradorNode) => {
    const { colaborador } = node;
    const hasSubordinados = node.subordinados.length > 0;
    const isSelected = selectedColaborador?.id === colaborador.id;
    const departamento = departamentos?.find((dep) => dep?.id === colaborador.departamentoId);
    const isGestor = colaborador.isGestor;
  // Novo cálculo: gestores mostram somente um badge vermelho com TOTAL (ele + toda a hierarquia abaixo);
  // não gestores mostram apenas seus próprios cards vencidos.
  const aggregatedTotal = isGestor ? node.totalCardsHierarquia : node.cardsVencidos;
  const showBadge = aggregatedTotal > 0;
    return (
      <div className="relative">
        <div className="flex flex-col items-center">
          <button
            onClick={(e) => handleColaboradorClick(colaborador, e)}
            className="group transition-transform duration-200 hover:scale-105"
          >
            <div className="relative">
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center text-base font-semibold text-white shadow-lg overflow-hidden ${getColorFromString(
                  colaborador?.nome || 'default'
                )} ${isSelected ? 'ring-2 ring-blue-300' : ''} ${isGestor ? 'ring-2 ring-yellow-400' : ''}`}
              >
                {(colaborador as any)?.foto ? (
                  <img
                    src={(colaborador as any).foto}
                    alt={`Foto de ${colaborador?.nome}`}
                    className="w-full h-full object-cover rounded-full"
                  />
                ) : (
                  getInitials(colaborador?.nome || 'NN')
                )}
                {hasSubordinados && (
                  <button
                    onClick={(e) => toggleNode(node, e)}
                    className={`absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-white shadow-md border border-white flex items-center justify-center text-gray-600 transition-transform duration-200 ${
                      expandedNodes.includes(colaborador.id) ? 'rotate-180' : ''
                    }`}
                  >
                    <ChevronDownIcon className="w-3 h-3" />
                  </button>
                )}
              </div>
              {showBadge && (
                <button
                  onClick={(e) => handleNotificacaoClick(colaborador.id, isGestor ? 'equipe' : 'proprios', e)}
                  className="absolute top-0 left-0 -translate-x-1/2 -translate-y-1/2 z-10 bg-red-500 hover:bg-red-600 text-white text-[0.6rem] font-bold rounded-full min-w-5 h-5 px-0.5 flex items-center justify-center shadow-lg animate-pulse transition-colors cursor-pointer"
                  title={isGestor ? 'Clique para ver cards vencidos (total da hierarquia)' : 'Clique para ver seus cards vencidos'}
                >
                  {aggregatedTotal}
                </button>
              )}
            </div>
            <div className="mt-1 text-center w-24">
              <div className="text-[0.7rem] font-medium text-gray-900 truncate">
                {colaborador?.nome || 'Nome não informado'}
                {isGestor && <span className="text-yellow-500 ml-0.5">★</span>}
              </div>
              <div className="text-[0.65rem] text-gray-600 truncate">{colaborador.cargo}</div>
              <div className="text-[0.6rem] text-gray-500 truncate">{departamento?.nome}</div>
              {hasSubordinados && (
                <div className="text-[0.6rem] text-gray-400 mt-0.5">
                  {node.subordinados.length} subordinado{node.subordinados.length !== 1 ? 's' : ''}
                </div>
              )}
            </div>
          </button>
          {isSelected && (
            <ColaboradorPopover
              colaborador={colaborador}
              departamentos={departamentos}
              flows={flows}
              onClose={() => setSelectedColaborador(null)}
              onViewProfile={onNavigateToColaborador}
              onNavigateToFlow={onNavigateToFlow}
              userPermissions={userPermissions}
            />
          )}
        </div>
      </div>
    );
  };

  const renderLevel = (nodes: ColaboradorNode[]) => (
    <div className="flex justify-center gap-8 flex-wrap">
      {nodes
        .sort((a, b) => {
          const aIsGestor = a?.colaborador?.isGestor || false;
          const bIsGestor = b?.colaborador?.isGestor || false;
          if (aIsGestor !== bIsGestor) return bIsGestor ? 1 : -1;
          const aNome = a?.colaborador?.nome || '';
          const bNome = b?.colaborador?.nome || '';
          return aNome.localeCompare(bNome);
        })
        .map((node) => (
          <div key={node.colaborador.id} className="flex flex-col items-center">
            {renderColaborador(node)}
            {expandedNodes.includes(node.colaborador.id) && node.subordinados.length > 0 && (
              <div className="mt-4 relative">
                <div className="absolute top-0 left-1/2 w-px h-4 bg-gray-300 -translate-x-1/2" />
                {renderLevel(node.subordinados)}
              </div>
            )}
          </div>
        ))}
    </div>
  );

  return (
    <div ref={containerRef} className={`min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-4 px-2 kanban-desktop-scale departamentos-compact ${
      isFullscreen ? 'fixed inset-0 z-50 overflow-auto' : ''
    }`}>
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-blue-200/20 to-purple-200/20 rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-br from-purple-200/20 to-pink-200/20 rounded-full blur-3xl transform translate-x-1/2 translate-y-1/2"></div>
      </div>
      <div className="w-full px-2 mx-auto relative z-10">
        <section className="relative">
          <div className="p-3 flex flex-col md:flex-row md:items-start md:justify-between gap-2">
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-gray-900 to-black text-white shadow-lg">
                <BuildingOfficeIcon className="h-6 w-6" />
              </div>
              <div>
                <h1 className="text-lg font-extrabold text-gray-900">Organograma</h1>
                <p className="text-[0.7rem] text-gray-600">Clique nos colaboradores para ver mais detalhes</p>
                <p className="text-[0.6rem] text-yellow-600 mt-0.5">★ Indica que o colaborador é gestor</p>
              </div>
            </div>
            <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-end">
              <div className="flex items-center gap-2">
                <FunnelIcon className="h-5 w-5 text-gray-500" />
                <select
                  aria-label="Filtrar por departamento"
                  value={selectedDepartamento}
                  onChange={(e) => {
                    setSelectedDepartamento(e.target.value);
                    setSelectedColaborador(null);
                    setExpandedNodes([]);
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent w-full md:w-auto"
                >
                  <option value="">Todos os departamentos</option>
                  {departamentos?.map((dep) => (
                    <option key={dep.id} value={dep.id}>
                      {dep.nome}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex gap-2 self-end md:self-auto">
                <button onClick={handleZoomOut} className="p-1 rounded-lg bg-gray-100 hover:bg-gray-200" title="Diminuir">
                  <MinusIcon className="h-4 w-4" />
                </button>
                <button onClick={handleZoomIn} className="p-1 rounded-lg bg-gray-100 hover:bg-gray-200" title="Aumentar">
                  <PlusIcon className="h-4 w-4" />
                </button>
                <button 
                  onClick={toggleFullscreen} 
                  className="p-1 rounded-lg bg-gray-100 hover:bg-gray-200" 
                  title={isFullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
                >
                  {isFullscreen ? (
                    <ArrowsPointingInIcon className="h-4 w-4" />
                  ) : (
                    <ArrowsPointingOutIcon className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="mt-4">
            <div className="w-full">
              <div className="w-full overflow-x-auto overflow-y-visible pt-6 pb-8 min-h-[400px]" onClick={handleClickOutside}>
                <div className="min-w-max flex justify-center">
                  <div
                    className="flex flex-col items-center gap-12 transition-transform duration-200"
                    style={{ transform: `scale(${zoom})`, transformOrigin: 'top center' }}
                  >
                    {hierarchy.length > 0 ? (
                      renderLevel(hierarchy)
                    ) : (
                      <div className="text-gray-500 text-sm">Nenhum colaborador encontrado para o filtro atual.</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {renderCardsVencidosPopover()}
        </section>
      </div>
    </div>
  );
}

export default Organograma;